import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import queryString from 'query-string'
import {
  Flex,
  Box,
  Label,
  Checkbox,
  Text,
  Link,
  FormField,
  Select,
  theme
} from 'pcln-design-system'
import { Calendar } from '@pcln/date-picker'
import StyledHeading from '@/components/StyledHeading'
import CalendarWrapperBox from '@/components/CalenderWrapperBox.styled'
import DateField from '@/components/DateField'
import ShadowEffect from '@/components/ShadowEffect'
import SearchFormButton from '@/components/SearchFormButton'
import TypeAhead from '@/components/TypeAhead'
import {
  formatDate,
  getPackagesDateRange,
  addFormattedDays,
  defaultMaxDate,
  defaultToday,
  getRcMinDate,
  getRcMaxDate,
  getTimeOptions
} from '@/shared-utils/date-helper'
import { PopoverContainer } from '@/components/Popover'
import { VP_DISCLAIMER } from '@/components/TravelerSelection/constants'
import analytics from '@/shared-utils/analytics'
import TravelerSelection from '@/components/TravelerSelection/TravelerSelection'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import useTabGroup from '@/hooks/useTabGroup'
import useContentfulSearchFormHeadlines from '@/hooks/useContentfulSearchFormHeadlines'
import {
  fields,
  initialState,
  CALENDAR_POPOVER_SELECTOR,
  getTextString
} from './utils'
import { isHotelPlusCar, hasHotel, STAY_FLY, hasFlight } from './TripTypes'
import TripTypeRadios from './TripTypeRadios'
import {
  isInfantOnInternationalTravel,
  isChildrenAgeProvided,
  isInfantLimitReachedPackages,
  isRoomCompacityReached,
  searchFormSchema
} from './validation'
import FormContainer from './FormContainer'
import { MakeWatchObject, PackagesFormStateType } from '../types'

const InlineBlockBox = styled(Box)`
  display: inline-block;
`

const FlexLabel = styled(Label)`
  display: flex;
  align-items: center;
`

const trackOneWayFlightLinkClick = () => {
  analytics.fireEvent({
    action: 'Search Form Link',
    label: 'I need a one-way flight'
  })
}

const StyledSelect = styled(Select)`
  background: ${theme.colors.white};
  &::-ms-expand {
    display: none;
  }
  cursor: pointer;
`

type TimeFieldProps = {
  name: string
  label: string
}

function TimeField({
  name,
  label,
  ...props
}: TimeFieldProps & Record<string, unknown>) {
  return (
    <FormField>
      <Label>{label}</Label>
      <StyledSelect {...props} id={name} name={name}>
        {getTimeOptions()}
      </StyledSelect>
    </FormField>
  )
}

type FormatOneWayHref = Pick<
  MakeWatchObject<PackagesFormStateType>,
  | 'watchTripType'
  | 'watchStartLocation'
  | 'watchEndLocation'
  | 'watchStartDate'
  | 'watchEndDate'
  | 'watchRoomCount'
  | 'watchAdultCount'
  | 'watchChildrenCount'
  | 'watchChildrenAges'
  | 'watchHotelStartDate'
  | 'watchHotelEndDate'
  | 'watchPartialStay'
>

const formatOneWayHref = ({
  watchTripType,
  watchStartLocation: startLocation,
  watchEndLocation: endLocation,
  watchStartDate,
  watchEndDate,
  watchRoomCount,
  watchAdultCount,
  watchChildrenCount,
  watchChildrenAges,
  watchHotelStartDate,
  watchHotelEndDate,
  watchPartialStay
}: FormatOneWayHref) => {
  const stringifiedQuery = queryString.stringify({
    'flight-code': 'OW',
    'trip-type': watchTripType,
    'origin-item': startLocation?.itemName,
    'origin-id': startLocation?.id,
    'origin-type': startLocation?.type,
    'origin-city': startLocation?.cityName,
    'origin-state': startLocation?.provinceName,
    'origin-state-code': startLocation?.stateCode,
    'origin-country': startLocation?.country,
    'origin-country-code': startLocation?.countryCode,
    'destination-item': endLocation?.itemName,
    'destination-id': endLocation?.id,
    'destination-type': endLocation?.type,
    'destination-city': endLocation?.cityName,
    'destination-state': endLocation?.provinceName,
    'destination-state-code': endLocation?.stateCode,
    'destination-country': endLocation?.country,
    'destination-country-code': endLocation?.countryCode,
    start: watchStartDate,
    end: watchEndDate,
    rooms: watchRoomCount,
    adults: watchAdultCount,
    children: watchChildrenCount,
    ages: JSON.stringify(watchChildrenAges),
    ...(watchPartialStay
      ? {
          'hotel-start': watchHotelStartDate,
          'hotel-end': watchHotelEndDate
        }
      : {})
  })

  return `/shop/search/trips/?${stringifiedQuery}`
}

type FormProps = {
  onSubmit: (data: PackagesFormStateType) => void
  isLandingPage?: boolean
}

function Form({ onSubmit, isLandingPage = true }: FormProps) {
  const { registerTabElement, focusNextElement } = useTabGroup()
  const {
    customer,
    isMobile = false,
    webstats
  } = useContext(BootstrapDataContext)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isRcPickupCalendarOpen, setIsRcPickupCalendarOpen] = useState(false)
  const [isRcDropoffCalendarOpen, setIsRcDropoffCalendarOpen] = useState(false)
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)

  const primaryHeadingText = 'Save up to $625 on your '

  const buttonText = 'Find Your Trip'
  const callToAction =
    'Book a hotel, flight or car with free cancellation for flexibility'
  const radioExperimentInitialState = {
    ...initialState,
    tripType: STAY_FLY
  }

  const searchForm = useContentfulSearchFormHeadlines('searchForm')

  const maxDaysHotelToSelect = 30
  return (
    <FormContainer
      onSubmit={onSubmit}
      fields={fields}
      initialState={radioExperimentInitialState}
      searchFormSchema={searchFormSchema}
    >
      {({
        setValue,
        errors,
        register,
        watchState: {
          watchTripType,
          watchAdultCount,
          watchRoomCount,
          watchChildrenAges,
          watchChildrenCount,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchPickupCarSomewhere,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime
        }
      }) => {
        const finalPrimaryHeadingText =
          getTextString(watchTripType)(primaryHeadingText)
        const contentfulHeadline = isMobile
          ? searchForm?.mobileHeadline
          : searchForm?.headline
        const headline = contentfulHeadline || finalPrimaryHeadingText

        const carLocationLabel = 'City, Airport or Address'

        const rudderstack = {
          'data-rudder-event': true,
          'data-sub-type': 'search',
          'data-cguid': webstats.clientGUID,
          'data-parent-product': watchTripType,
          'data-source': isLandingPage ? 'package landing' : 'homepage',
          'data-email-address': customer.emailAddress
        }

        const maxDaysToSelect = !hasHotel(watchTripType)
          ? 330
          : maxDaysHotelToSelect

        const childAgeSelectors = {
          optionsText: {
            infant: hasFlight(watchTripType) ? '0-1 infant in lap' : 'Infant',
            oneYear: hasFlight(watchTripType)
              ? '0-1 infant in seat'
              : '1 year old'
          }
        }

        return (
          <>
            {isLandingPage && (
              <StyledHeading
                pb={1}
                tag="h1"
                color="text.heading"
                text={headline}
                textStyle={['heading3', null, null, null, 'heading2']}
              />
            )}
            <Box>
              <TripTypeRadios
                watchTripType={watchTripType}
                setValue={setValue}
              />
              <Flex flexWrap="wrap" mx={-2}>
                {!isHotelPlusCar(watchTripType) && (
                  <Box width={[1, null, 1 / 2]} px={2} mt={3}>
                    <ShadowEffect enabled forProperty="startLocation">
                      {({
                        disableShadowState
                      }: {
                        disableShadowState: () => void
                      }) => (
                        <TypeAhead
                          searchProduct="packagesOrigin"
                          label="Where from?"
                          placeholder="Where from?"
                          searchKey="startLocation"
                          setValue={setValue}
                          errors={errors}
                          disableShadowState={disableShadowState}
                          defaultSelectedItem={watchStartLocation}
                          ref={registerTabElement}
                          focusNextElement={focusNextElement}
                        />
                      )}
                    </ShadowEffect>
                  </Box>
                )}
                <Box
                  width={[1, null, isHotelPlusCar(watchTripType) ? 1 : 1 / 2]}
                  px={2}
                  mt={[isHotelPlusCar(watchTripType) ? 3 : 0, null, 3]}
                >
                  <ShadowEffect enabled forProperty="endLocation">
                    {({
                      disableShadowState
                    }: {
                      disableShadowState: () => void
                    }) => (
                      <TypeAhead
                        searchProduct="packagesDestination"
                        label="Where to?"
                        placeholder="Where to?"
                        searchKey="endLocation"
                        errors={errors}
                        setValue={setValue}
                        disableShadowState={disableShadowState}
                        defaultSelectedItem={watchEndLocation}
                        onItemSelect={item => {
                          // auto-populate RC location if not set yet
                          if (!watchRcLocation) {
                            setValue('rcLocation', item, { shouldDirty: true })
                          }
                        }}
                        ref={registerTabElement}
                        focusNextElement={focusNextElement}
                      />
                    )}
                  </ShadowEffect>
                </Box>
              </Flex>
              {watchTripType.includes('FLY') && (
                <InlineBlockBox mt={[0, null, 3]}>
                  <Link
                    href={formatOneWayHref({
                      watchTripType,
                      watchStartLocation,
                      watchEndLocation,
                      watchStartDate,
                      watchEndDate,
                      watchRoomCount,
                      watchAdultCount,
                      watchChildrenCount,
                      watchChildrenAges,
                      watchHotelStartDate,
                      watchHotelEndDate,
                      watchPartialStay
                    })}
                    target="_blank"
                    onClick={trackOneWayFlightLinkClick}
                  >
                    <Text fontWeight="bold">I need a one-way flight</Text>
                  </Link>
                </InlineBlockBox>
              )}
              <Flex flexWrap="wrap" alignItems="flex-start" mx={-2}>
                <Flex width={[1, null, null, null, 2 / 3]} flexWrap="wrap">
                  <CalendarWrapperBox
                    px={2}
                    mt={isHotelPlusCar(watchTripType) && isMobile ? 0 : 3}
                    width={[1, null, 1 / 2]}
                  >
                    <ShadowEffect enabled forProperty="startDate">
                      {({
                        disableShadowState
                      }: {
                        disableShadowState: () => void
                      }) => (
                        <PopoverContainer
                          data-calendar
                          open={isCalendarOpen}
                          onDismiss={() => {
                            setIsCalendarOpen(false)
                            disableShadowState()
                          }}
                          domSelectorForVisuallyContainedElement={
                            CALENDAR_POPOVER_SELECTOR
                          }
                        >
                          <DateField
                            name="travelDates"
                            data-testid="packages-date-range"
                            label={
                              isHotelPlusCar(watchTripType)
                                ? 'Check-in   -   Check-out'
                                : 'Departing - Returning'
                            }
                            ref={registerTabElement}
                            value={getPackagesDateRange({
                              startDate: watchStartDate,
                              endDate: watchEndDate
                            })}
                            onFocus={() => {
                              setIsCalendarOpen(true)
                            }}
                            error={(() => {
                              if (errors.startDate) {
                                return errors.startDate.message
                              }
                              if (errors.endDate) {
                                return errors.endDate.message
                              }
                            })()}
                            hideTooltip={false}
                            aria-expanded={isCalendarOpen ? 'true' : 'false'}
                          />

                          <Calendar
                            roundedCorners
                            mmonthWidthPx={350}
                            startDate={watchStartDate}
                            endDate={watchEndDate}
                            maxDaysToSelect={maxDaysToSelect}
                            onChange={(
                              _: unknown,
                              {
                                startDate,
                                endDate
                              }: { startDate: string; endDate: string }
                            ) => {
                              if (startDate && endDate) {
                                setIsCalendarOpen(false)
                                disableShadowState()
                              }
                              setValue('startDate', startDate || '', {
                                shouldDirty: true
                              })
                              setValue('endDate', endDate || '', {
                                shouldDirty: true
                              })
                              setValue('hotelStartDate', startDate || '', {
                                shouldDirty: true
                              })
                              setValue('hotelEndDate', endDate || '', {
                                shouldDirty: true
                              })

                              // auto-populate RC dates if not set yet
                              if (!watchRcPickupDate)
                                setValue('rcPickupDate', startDate, {
                                  shouldDirty: true
                                })
                              if (!watchRcDropoffDate)
                                setValue('rcDropoffDate', endDate, {
                                  shouldDirty: true
                                })
                            }}
                            useDefaultFooter
                            onDismiss={() => {
                              setIsCalendarOpen(false)
                              disableShadowState()
                            }}
                          />
                        </PopoverContainer>
                      )}
                    </ShadowEffect>
                  </CalendarWrapperBox>
                  <Box px={2} mt={[0, null, 3]} width={[1, null, 1 / 2]}>
                    <ShadowEffect
                      enabled
                      forProperty="travelers-selection-readonly-input"
                      alternativeBlurHandling
                    >
                      {({
                        disableShadowState
                      }: {
                        disableShadowState: () => void
                      }) => (
                        <TravelerSelection
                          disclaimer={VP_DISCLAIMER}
                          onDoneButtonClick={disableShadowState}
                          roomCount={watchRoomCount}
                          adultCount={watchAdultCount}
                          childrenAges={watchChildrenAges}
                          childrenCount={watchChildrenCount}
                          showChildAgeSelectors
                          showRoomMenuItem={hasHotel(watchTripType)}
                          showSubLabel={false}
                          setValue={setValue}
                          disableShadowState={disableShadowState}
                          isMobile={isMobile}
                          error={
                            isInfantOnInternationalTravel(
                              watchStartLocation,
                              watchEndLocation,
                              watchChildrenAges
                            ) ||
                            isInfantLimitReachedPackages(
                              watchAdultCount,
                              watchChildrenAges,
                              watchTripType
                            ) ||
                            isChildrenAgeProvided(
                              watchChildrenCount,
                              watchChildrenAges
                            ) ||
                            isRoomCompacityReached(
                              watchAdultCount,
                              watchChildrenCount
                            )
                          }
                          infantCount={0}
                          showInfantMenuItem={false}
                          showYouthMenuItem={false}
                          youthCount={0}
                          childAgeSelectors={childAgeSelectors}
                        />
                      )}
                    </ShadowEffect>
                  </Box>
                  <Box mt={2} width={1}>
                    <Flex ml={1} flexWrap={['wrap', null, 'nowrap']}>
                      {!isHotelPlusCar(watchTripType) &&
                        hasHotel(watchTripType) && (
                          <FlexLabel nowrap fontSize={0} mr={2}>
                            <Checkbox
                              id="packages-partial-stay"
                              {...register('partialStay')}
                            />{' '}
                            I only need a hotel for part of my stay
                          </FlexLabel>
                        )}
                    </Flex>
                    {watchPartialStay && (
                      <Box width={[1, null, 1 / 2]} mt={2} px={2}>
                        <ShadowEffect enabled forProperty="hotelDates">
                          {({
                            disableShadowState
                          }: {
                            disableShadowState: () => void
                          }) => (
                            <PopoverContainer
                              data-hotel-calendar="true"
                              domSelectorForVisuallyContainedElement={
                                CALENDAR_POPOVER_SELECTOR
                              }
                              onDismiss={() => {
                                setIsHotelCalendarOpen(false)
                                disableShadowState()
                              }}
                              open={isHotelCalendarOpen}
                            >
                              <DateField
                                aria-expanded={
                                  isHotelCalendarOpen ? 'true' : 'false'
                                }
                                error={(() => {
                                  if (errors.hotelStartDate) {
                                    return errors.hotelStartDate.message
                                  }
                                  if (errors.hotelEndDate) {
                                    return errors.hotelEndDate.message
                                  }
                                })()}
                                label="Check-in - Check-out"
                                name="hotelDates"
                                onFocus={() => {
                                  setIsHotelCalendarOpen(true)
                                }}
                                value={getPackagesDateRange({
                                  startDate: watchHotelStartDate ?? null,
                                  endDate: watchHotelEndDate ?? null
                                })}
                              />
                              <Calendar
                                startDate={watchHotelStartDate}
                                endDate={watchHotelEndDate}
                                maxDate={
                                  watchEndDate
                                    ? addFormattedDays(watchEndDate, 1)
                                    : defaultMaxDate
                                }
                                minDate={watchStartDate || defaultToday}
                                maxDaysToSelect={maxDaysToSelect}
                                roundedCorners
                                monthWidthPx={350}
                                onChange={(
                                  _: unknown,
                                  {
                                    startDate,
                                    endDate
                                  }: { startDate: string; endDate: string }
                                ) => {
                                  if (startDate && endDate) {
                                    setIsHotelCalendarOpen(false)
                                    disableShadowState()
                                  }
                                  setValue('hotelStartDate', startDate || '', {
                                    shouldDirty: true
                                  })
                                  setValue('hotelEndDate', endDate || '', {
                                    shouldDirty: true
                                  })
                                }}
                                monthsToShow={
                                  watchStartDate && watchEndDate ? 1 : 2
                                }
                                onDismiss={() => {
                                  setIsHotelCalendarOpen(false)
                                  disableShadowState()
                                }}
                                useDefaultFooter
                              />
                            </PopoverContainer>
                          )}
                        </ShadowEffect>
                      </Box>
                    )}
                  </Box>
                </Flex>
                {isHotelPlusCar(watchTripType) && (
                  <Flex flexWrap="wrap" alignItems="flex-start">
                    <Flex mt={2}>
                      <FlexLabel nowrap fontSize={0} ml={1}>
                        <Checkbox
                          id="packages-pickup-car-somewhere"
                          data-testid="packages-pickup-car-somewhere"
                          {...register('pickupCarSomewhere')}
                        />{' '}
                        I want to pick up my car somewhere else
                      </FlexLabel>
                    </Flex>

                    {watchPickupCarSomewhere && (
                      <Flex
                        flexWrap="wrap"
                        alignItems="flex-start"
                        width={[1, null, 1]}
                        px={2}
                        mt={2}
                      >
                        <ShadowEffect enabled forProperty="rcLocation">
                          {({
                            disableShadowState
                          }: {
                            disableShadowState: () => void
                          }) => (
                            <TypeAhead
                              searchProduct="cars"
                              label={carLocationLabel}
                              placeholder={carLocationLabel}
                              searchKey="rcLocation"
                              data-testid="packages-rc-location"
                              errors={errors}
                              setValue={setValue}
                              disableShadowState={disableShadowState}
                              defaultSelectedItem={watchRcLocation ?? null}
                              ref={registerTabElement}
                              focusNextElement={focusNextElement}
                            />
                          )}
                        </ShadowEffect>
                        <Flex mt={2}>
                          <FlexLabel nowrap fontSize={0} ml={-2}>
                            <Checkbox
                              id="packages-pickup-car-include-airport"
                              {...register('pickupCarIncludeAirport')}
                            />{' '}
                            Include airport pick-up and drop-off locations
                          </FlexLabel>
                        </Flex>
                        <Flex flexWrap="wrap">
                          <CalendarWrapperBox
                            mt={3}
                            width={[1 / 2, null, 1 / 4]}
                            data-testid="packages-rc-pickup-date"
                            id="rc-pickup-date"
                          >
                            <ShadowEffect enabled forProperty="rcPickupDate">
                              {({
                                disableShadowState
                              }: {
                                disableShadowState: () => void
                              }) => (
                                <PopoverContainer
                                  data-calendar
                                  open={isRcPickupCalendarOpen}
                                  onDismiss={() => {
                                    setIsRcPickupCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                  domSelectorForVisuallyContainedElement={
                                    CALENDAR_POPOVER_SELECTOR
                                  }
                                >
                                  <DateField
                                    name="rcPickupDate"
                                    label="Pick-up"
                                    ref={registerTabElement}
                                    value={formatDate(
                                      watchRcPickupDate ?? null
                                    )}
                                    onFocus={() => {
                                      setIsRcPickupCalendarOpen(true)
                                    }}
                                    error={(() => {
                                      if (errors.rcPickupDate) {
                                        return errors.rcPickupDate.message
                                      }
                                    })()}
                                    hideTooltip={false}
                                    aria-expanded={
                                      isRcPickupCalendarOpen ? 'true' : 'false'
                                    }
                                  />
                                  <Calendar
                                    roundedCorners
                                    monthWidthPx={350}
                                    monthsToShow={2}
                                    selectTwoDates={false}
                                    maxDate={getRcMaxDate(watchRcDropoffDate)}
                                    startDate={watchRcPickupDate}
                                    onChange={(
                                      _: unknown,
                                      { startDate }: { startDate: string }
                                    ) => {
                                      if (startDate) {
                                        setIsRcPickupCalendarOpen(false)
                                        disableShadowState()
                                      }
                                      setValue(
                                        'rcPickupDate',
                                        startDate || '',
                                        { shouldDirty: true }
                                      )
                                    }}
                                    useDefaultFooter
                                    onDismiss={() => {
                                      setIsRcPickupCalendarOpen(false)
                                      disableShadowState()
                                    }}
                                  />
                                </PopoverContainer>
                              )}
                            </ShadowEffect>
                          </CalendarWrapperBox>
                          <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                            <ShadowEffect enabled forProperty="rcPickupTime">
                              <TimeField
                                id="rc-pickup-time"
                                data-testid="packages-rc-pickup-time"
                                name="rcPickupTime"
                                label="Pick-up Time"
                                style={{ height: isMobile ? '46px' : '56px' }}
                                value={watchRcPickupTime}
                                onChange={(
                                  e: React.ChangeEvent<HTMLSelectElement>
                                ) => {
                                  setValue(
                                    'rcPickupTime',
                                    e.currentTarget.value
                                  )
                                  e.target.blur()
                                }}
                              />
                            </ShadowEffect>
                          </Box>
                          <CalendarWrapperBox
                            mt={3}
                            width={[1 / 2, null, 1 / 4]}
                            id="rc-dropoff-date"
                            data-testid="packages-rc-dropoff-date"
                          >
                            <ShadowEffect enabled forProperty="rcDropoffDate">
                              {({
                                disableShadowState
                              }: {
                                disableShadowState: () => void
                              }) => (
                                <PopoverContainer
                                  data-calendar
                                  open={isRcDropoffCalendarOpen}
                                  onDismiss={() => {
                                    setIsRcDropoffCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                  domSelectorForVisuallyContainedElement={
                                    CALENDAR_POPOVER_SELECTOR
                                  }
                                >
                                  <DateField
                                    name="rcDropoffDate"
                                    label="Drop-off"
                                    ref={registerTabElement}
                                    value={formatDate(
                                      watchRcDropoffDate ?? null
                                    )}
                                    onFocus={() => {
                                      setIsRcDropoffCalendarOpen(true)
                                    }}
                                    error={(() => {
                                      if (errors.rcDropoffDate) {
                                        return errors.rcDropoffDate.message
                                      }
                                    })()}
                                    hideTooltip={false}
                                    aria-expanded={
                                      isRcDropoffCalendarOpen ? 'true' : 'false'
                                    }
                                  />
                                  <Calendar
                                    monthsToShow={2}
                                    roundedCorners
                                    monthWidthPx={350}
                                    selectTwoDates={false}
                                    minDate={getRcMinDate(watchRcPickupDate)}
                                    startDate={watchRcDropoffDate}
                                    onChange={(
                                      _: unknown,
                                      { startDate }: { startDate: string }
                                    ) => {
                                      if (startDate) {
                                        setIsRcDropoffCalendarOpen(false)
                                        disableShadowState()
                                      }
                                      setValue(
                                        'rcDropoffDate',
                                        startDate || '',
                                        { shouldDirty: true }
                                      )
                                    }}
                                    useDefaultFooter
                                    onDismiss={() => {
                                      setIsRcDropoffCalendarOpen(false)
                                      disableShadowState()
                                    }}
                                  />
                                </PopoverContainer>
                              )}
                            </ShadowEffect>
                          </CalendarWrapperBox>
                          <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                            <ShadowEffect enabled forProperty="rcDropoffTime">
                              <TimeField
                                id="rc-dropoff-time"
                                name="rcDropoffTime"
                                data-testid="packages-rc-dropoff-time"
                                label="Drop-off Time"
                                style={{ height: isMobile ? '46px' : '56px' }}
                                value={watchRcDropoffTime}
                                onChange={(
                                  e: React.ChangeEvent<HTMLSelectElement>
                                ) => {
                                  setValue(
                                    'rcDropoffTime',
                                    e.currentTarget.value
                                  )
                                  e.target.blur()
                                }}
                              />
                            </ShadowEffect>
                          </Box>
                        </Flex>
                      </Flex>
                    )}
                    <Flex flexWrap="wrap" width={[1, null, null, null, 1]}>
                      <Box width={[1, null, 1 / 2]} px={2} mt={3}>
                        <SearchFormButton
                          buttonText={buttonText}
                          isMobile={isMobile}
                          data-testid="find-your-trip-button"
                          {...rudderstack}
                        />
                      </Box>
                      <Box width={[1, null, 0.4]} px={2} mt={3}>
                        <Text fontSize="12px" mt={2}>
                          {callToAction}
                        </Text>
                      </Box>
                    </Flex>
                  </Flex>
                )}
                {!isHotelPlusCar(watchTripType) && (
                  <Flex
                    flexDirection="column"
                    width={[1, null, null, null, 1 / 3]}
                    px={2}
                    mt={3}
                    ml="auto"
                  >
                    <SearchFormButton
                      buttonText={buttonText}
                      isMobile={isMobile}
                      data-testid="find-your-trip-button"
                      {...rudderstack}
                    />
                    <Text fontSize="12px" mt={2}>
                      {callToAction}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Box>
          </>
        )
      }}
    </FormContainer>
  )
}

export default Form
